import { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Toast, spacing, ColumnConfig, ActionsMenu } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { withTranslation } from '../../../../../locales/hoc';
import { TeamMemberDataType, TeamMembersTable } from '../TeamMembersTable';
import { TeamRoleCell } from './TeamRoleCell';
import { useUpdateOrganisationTeamMemberMutation } from '../__generated__/updateTeamMember.mutation.generated';
import { AssignButton } from '../AssignButton';
import { useRemoveMemberFromTeam } from '../useRemoveMemberFromTeam';
import { OrganisationTeamRole } from '../../../../../__generated__/types';
import { EmailCell } from '../../../../view/components/EmailCell';
import { PressableCell } from '../../../../components/PressableCell';
import { useGoToProfile } from '../../../../components/useGoToProfile';

interface Props {
	data: TeamMemberDataType[];
	organisationUuid: string;
	teamUuid: string;
	viewOnly?: boolean;
}

export const TeamCaregiversTable = withTranslation(
	({ data, teamUuid, organisationUuid, viewOnly = false }: Props) => {
		const { t } = useTranslation();
		const goToProfile = useGoToProfile();
		const [updateTeamMember] = useUpdateOrganisationTeamMemberMutation();
		const { removeMemberFromTeam } = useRemoveMemberFromTeam(teamUuid);

		const handleRoleChange = useCallback(
			async (memberId: string, teamRole: OrganisationTeamRole) => {
				try {
					await updateTeamMember({
						variables: {
							data: {
								memberId,
								teamUuid,
								role: teamRole,
							},
						},
					});
					Toast.show({
						type: 'success',
						text1: t('toasts.success.role-updated'),
					});
				} catch {
					Toast.show({
						type: 'error',
						text1: t('toasts.error.something-wrong'),
					});
				}
			},
			[t, teamUuid, updateTeamMember],
		);

		const renderRowActions = useCallback(
			({ row }: { row: TeamMemberDataType }) => (
				<ActionsMenu
					actions={[
						{
							title: t('table.actions.view-profile'),
							onPress: () => goToProfile(row),
						},
						{
							title: t('table.actions.remove-from-team'),
							onPress: removeMemberFromTeam(row),
							hidden: viewOnly,
						},
					]}
				/>
			),
			[viewOnly, t, removeMemberFromTeam, goToProfile],
		);

		const COLUMNS_CONFIG: ColumnConfig<TeamMemberDataType>[] = useMemo(
			() => [
				{
					name: t('table.header.name'),
					renderCell: ({ row }) => (
						<PressableCell text={getName(row)} onPress={() => goToProfile(row)} />
					),
				},
				{
					name: t('table.header.username'),
					accessor: 'username' as const,
				},
				{
					name: t('table.header.role'),
					renderCell: ({ row }) => (
						<TeamRoleCell row={row} onRoleChange={viewOnly ? undefined : handleRoleChange} />
					),
				},
				{
					name: t('table.header.email'),
					accessor: 'email' as const,
					renderCell: ({ row }) => <EmailCell email={row.email} />,
				},
			],
			[goToProfile, handleRoleChange, t, viewOnly],
		);

		return (
			<View style={styles.container}>
				{viewOnly === false && (
					<AssignButton organisationUuid={organisationUuid} teamUuid={teamUuid} type="caregivers" />
				)}
				<TeamMembersTable
					data={data}
					columnsConfig={COLUMNS_CONFIG}
					renderRowActions={renderRowActions}
				/>
			</View>
		);
	},
);

const styles = StyleSheet.create({
	container: {
		gap: spacing(2),
	},
});
